import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Users,
  MessageCircle,
  Calendar,
  Home,
  Settings,
  ShieldCheck,
  CreditCard,
  Image,
  Rss,
  User,
  History,
  Star
} from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { Badge } from '@/components/ui/badge';

interface EscortDashboardNavigationProps {
  collapsed?: boolean;
  totalUnread?: number;
}

const EscortDashboardNavigation: React.FC<EscortDashboardNavigationProps> = ({ collapsed = false, totalUnread = 0 }) => {
  const location = useLocation();
  const { t } = useLanguage();
  
  const isActive = (path: string) => {
    return location.pathname === `/escort-dashboard${path}`;
  };

  const menuItems = [
    {
      href: '',
      icon: Home,
      label: t('escort:dashboard.overview'),
    },
    {
      href: '/profile',
      icon: User,
      label: t('escort:dashboard.profile'),
    },
    {
      href: '/messages',
      icon: MessageCircle,
      label: t('escort:dashboard.messages'),
      badgeCount: totalUnread
    },
    {
      href: '/bookings',
      icon: History,
      label: t('escort:dashboard.bookings'),
    },
    {
      href: '/calendar',
      icon: Calendar,
      label: t('escort:dashboard.calendar'),
    },
    {
      href: '/media',
      icon: Image,
      label: t('escort:dashboard.media'),
    },
    {
      href: '/finances',
      icon: CreditCard,
      label: t('escort:dashboard.finances'),
    },
    {
      href: '/premium',
      icon: Star,
      label: t('escort:dashboard.premium'),
    },
    {
      href: '/security',
      icon: ShieldCheck,
      label: t('escort:dashboard.security'),
    },
  ];

  return (
    <nav className={cn("space-y-1 w-full", collapsed ? "px-1" : "px-2")}>
      {menuItems.map((item) => (
        <Link
          key={item.href}
          to={`/escort-dashboard${item.href}`}
          title={collapsed ? item.label : undefined}
          className={cn(
            'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group',
            isActive(item.href)
              ? 'bg-pink-50 text-pink-700'
              : 'text-gray-700 hover:bg-pink-50 hover:text-pink-700',
            collapsed ? "justify-center" : ""
          )}
        >
          <item.icon
            className={cn(
              'h-5 w-5 flex-shrink-0', 
              collapsed ? "" : "mr-3",
              isActive(item.href) ? 'text-pink-500' : 'text-gray-500 group-hover:text-pink-600'
            )}
          />
          {!collapsed && (
            <span className="flex-1 flex justify-between items-center">
                {item.label}
                {item.badgeCount && item.badgeCount > 0 && (
                    <Badge variant="destructive" className="h-5 px-1.5 text-xs rounded-full ml-auto">
                        {item.badgeCount > 99 ? '99+' : item.badgeCount}
                    </Badge>
                )}
            </span>
          )}
        </Link>
      ))}
    </nav>
  );
};

export default EscortDashboardNavigation;
